import { useState, useRef, useEffect } from "react";
import { MessageSquare, EllipsisVertical, Pin, Pencil, Trash2 } from "lucide-react";

import { useAuth } from "@/lib/auth";
import {
  useComments,
  useCreateComment,
  useCreateGuestComment,
  useDeleteComment,
  useUpdateComment,
  useTogglePinComment,
} from "@/hooks/useComments";
import { useGuestName } from "./GuestNameBanner";
import { ConfirmDialog } from "@/components/dashboard/ConfirmDialog";

function timeAgo(dateStr: string): string {
  const seconds = Math.floor(
    (Date.now() - new Date(dateStr).getTime()) / 1000,
  );
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CommentSectionProps {
  /** Playlist context — pass for performance-level comments. */
  playlistId?: string | null;
  /** Course context — pass for course-level comments. */
  courseId?: string | null;
  /** Guest share token for unauthenticated posting. */
  guestToken?: string | null;
  /** Whether the guest token is a playlist invitation or a course share. */
  guestTokenType?: "playlist" | "course";
  /** The playlist/course owner's user ID — used to show organizer badge + delete. */
  organizerUserId?: string | null;
  /** Extra className on the outer <section>. */
  className?: string;
}

// ---------------------------------------------------------------------------
// CommentItem
// ---------------------------------------------------------------------------

interface CommentItemProps {
  comment: any;
  isOrganizer: boolean;
  currentUserId: string | undefined;
  onDelete: (id: string) => Promise<void>;
  onUpdate: (id: string, body: string) => Promise<void>;
  onTogglePin: (id: string, pinned: boolean) => Promise<void>;
}

function CommentItem({
  comment: c,
  isOrganizer,
  currentUserId,
  onDelete,
  onUpdate,
  onTogglePin,
}: CommentItemProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editBody, setEditBody] = useState(c.body);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [saving, setSaving] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const editRef = useRef<HTMLTextAreaElement>(null);

  const isOrgComment = c.user_id != null;
  const isOwnComment = !!currentUserId && c.user_id === currentUserId;
  const isPinned = !!c.pinned_at;

  const initials = c.display_name
    .split(" ")
    .map((w: string) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [menuOpen]);

  // Auto-resize edit textarea
  useEffect(() => {
    const el = editRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [editBody, isEditing]);

  // Focus textarea when entering edit mode
  useEffect(() => {
    if (isEditing) editRef.current?.focus();
  }, [isEditing]);

  async function handleSaveEdit() {
    const trimmed = editBody.trim();
    if (!trimmed || trimmed === c.body) {
      setIsEditing(false);
      setEditBody(c.body);
      return;
    }
    setSaving(true);
    try {
      await onUpdate(c.id, trimmed);
      setIsEditing(false);
    } catch {
      // Keep edit mode open so the user can retry
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div
        className={`flex gap-3 rounded-[1rem] border px-4 py-3 ${
          isPinned
            ? "border-[#762a5c]/20 bg-[#762a5c]/[0.03]"
            : "border-[#e2e4e9] bg-white"
        }`}
      >
        <div
          className={`flex h-8 w-8 flex-none items-center justify-center rounded-full text-xs font-bold ${
            isOrgComment
              ? "bg-[#762a5c]/10 text-[#762a5c]"
              : "bg-[#e8e4e7] text-[#70696f]"
          }`}
        >
          {initials}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-[0.85rem] font-semibold text-[#321b29]">
              {c.display_name}
            </span>
            {isOrgComment && (
              <span className="rounded-full bg-[#762a5c]/10 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-[#762a5c]">
                Organizer
              </span>
            )}
            {isPinned && (
              <span className="flex items-center gap-1 rounded-full bg-[#762a5c]/10 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-[#762a5c]">
                <Pin className="h-3 w-3" />
                Pinned
              </span>
            )}
            <span className="text-[0.75rem] text-[#b0a8ad]">
              {timeAgo(c.created_at)}
            </span>
            {c.updated_at && (
              <span className="text-[0.72rem] italic text-[#b0a8ad]">
                (edited)
              </span>
            )}
          </div>

          {isEditing ? (
            <div className="mt-1.5">
              <textarea
                ref={editRef}
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                maxLength={2000}
                rows={2}
                className="w-full resize-none rounded-[0.6rem] border border-[#e2e4e9] bg-[#fafafa] px-3 py-2 text-[0.88rem] text-[#321b29] outline-none focus:border-[#762a5c]/40"
              />
              <div className="mt-1.5 flex gap-2">
                <button
                  type="button"
                  onClick={handleSaveEdit}
                  disabled={saving}
                  className="rounded-[0.5rem] bg-[#762a5c] px-4 py-1.5 text-[0.78rem] font-semibold text-white transition-colors hover:bg-[#5e2249] disabled:opacity-50"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    setEditBody(c.body);
                  }}
                  className="rounded-[0.5rem] border border-[#e2e4e9] px-4 py-1.5 text-[0.78rem] font-semibold text-[#7f777d] transition-colors hover:bg-[#f5f3f4]"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <p className="mt-1 whitespace-pre-wrap text-[0.88rem] leading-relaxed text-[#3a3338]">
              {c.body}
            </p>
          )}
        </div>

        {isOrganizer && !isEditing && (
          <div ref={menuRef} className="relative flex-none self-start">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="rounded-md p-1 text-[#b0a8ad] transition-colors hover:text-[#321b29]"
              title="Actions"
            >
              <EllipsisVertical className="h-4 w-4" />
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-full z-10 mt-1 min-w-[10rem] overflow-hidden rounded-[0.7rem] border border-[#e2e4e9] bg-white py-1 shadow-lg">
                {isOwnComment && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setEditBody(c.body);
                      setIsEditing(true);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-[0.83rem] text-[#321b29] hover:bg-[#f5f3f4]"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    onTogglePin(c.id, !isPinned);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[0.83rem] text-[#321b29] hover:bg-[#f5f3f4]"
                >
                  <Pin className="h-3.5 w-3.5" />
                  {isPinned ? "Unpin" : "Pin"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false);
                    setConfirmDelete(true);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-[0.83rem] text-[#ba4d4d] hover:bg-[#fdf2f2]"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete comment"
        description="This comment will be permanently removed. This action cannot be undone."
        confirmLabel="Delete"
        tone="danger"
        onConfirm={async () => {
          await onDelete(c.id);
          setConfirmDelete(false);
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// CommentSection
// ---------------------------------------------------------------------------

export function CommentSection({
  playlistId,
  courseId,
  guestToken,
  guestTokenType = "playlist",
  organizerUserId,
  className = "",
}: CommentSectionProps) {
  const { user } = useAuth();
  const isOrganizer = !!user && !!organizerUserId && user.id === organizerUserId;

  const filter = {
    playlistId: playlistId ?? undefined,
    courseId: courseId ?? undefined,
  };

  const { data: comments, refetch, setData: setComments } = useComments(filter);
  const { execute: postAuth, loading: postingAuth } = useCreateComment();
  const { execute: postGuest, loading: postingGuest } = useCreateGuestComment();
  const { execute: doDelete } = useDeleteComment();
  const { execute: doUpdate } = useUpdateComment();
  const { execute: doTogglePin } = useTogglePinComment();

  const [body, setBody] = useState("");
  const [postError, setPostError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const storedGuestName = useGuestName();
  const posting = postingAuth || postingGuest;
  const canGuestPost = !user && !!guestToken && storedGuestName.length > 0;
  const canPost = !!user || canGuestPost;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = body.trim();
    if (!trimmed) return;

    let result: { data: unknown; error: string | null };

    if (user) {
      const displayName =
        user.user_metadata?.display_name || user.email || "User";
      result = await postAuth(filter, user.id, displayName, trimmed);
    } else if (guestToken && storedGuestName) {
      result = await postGuest(guestToken, guestTokenType, storedGuestName, trimmed);
    } else {
      return;
    }

    if (result.error) {
      setPostError(result.error);
      return;
    }

    setPostError(null);
    setBody("");

    // Optimistic update: add the new comment immediately
    const newComment = result.data as any;
    if (newComment?.id) {
      setComments([...(comments ?? []), newComment]);
    }
    // Also refetch in background to sync with server
    refetch();
  }

  async function handleDelete(commentId: string) {
    const result = await doDelete(commentId);
    if (result.error) throw new Error(result.error);
    refetch();
  }

  async function handleUpdate(commentId: string, newBody: string) {
    const result = await doUpdate(commentId, newBody);
    if (result.error) throw new Error(result.error);
    refetch();
  }

  async function handleTogglePin(commentId: string, pinned: boolean) {
    const result = await doTogglePin(commentId, pinned);
    if (result.error) throw new Error(result.error);
    refetch();
  }

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight}px`;
    }
  }, [body]);

  const commentList = comments ?? [];

  // Don't render if no context provided
  if (!playlistId && !courseId) return null;

  return (
    <section className={className}>
      <h2 className="mb-2.5 flex items-center gap-2 font-[family-name:var(--font-display)] text-[clamp(1.1rem,0.95rem+0.5vw,1.35rem)] font-medium leading-[1.2] text-[#321b29]">
        <MessageSquare className="h-[1.1em] w-[1.1em] text-[#762a5c]" />
        Comments
        {commentList.length > 0 && (
          <span className="ml-1.5 text-[0.85rem] font-normal text-[#b0a8ad]">
            ({commentList.length})
          </span>
        )}
      </h2>

      {/* Comment list */}
      {commentList.length > 0 && (
        <div className="mb-6 space-y-3">
          {commentList.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              isOrganizer={isOrganizer}
              currentUserId={user?.id}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
              onTogglePin={handleTogglePin}
            />
          ))}
        </div>
      )}

      {/* Comment input */}
      {canPost && (
        <form
          onSubmit={handleSubmit}
          className="rounded-[1rem] border border-[#e2e4e9] bg-white p-4"
        >
          <textarea
            ref={textareaRef}
            placeholder="Write a comment..."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            rows={2}
            className="w-full resize-none rounded-[0.6rem] border border-[#e2e4e9] bg-[#fafafa] px-3 py-2 text-[0.88rem] text-[#321b29] placeholder:text-[#b0a8ad] outline-none focus:border-[#762a5c]/40"
          />
          {postError && (
            <p className="mt-2 text-[0.82rem] text-[#ba4d4d]">{postError}</p>
          )}
          <div className="mt-2 flex items-center justify-end">
            <button
              type="submit"
              disabled={posting || !body.trim()}
              className="rounded-[0.5rem] bg-[#762a5c] px-5 py-2.5 text-[0.82rem] font-semibold text-white transition-colors hover:bg-[#5e2249] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {posting ? "Posting..." : "Post"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
